"use client";

/**
 * PillHistory: the composer's history as a conversation between him and Bash
 * (spec 2026-09-24 history-as-chat §1; approved mock
 * .superpowers/sdd/2026-09-24-history-as-chat/mock.html), floating above the
 * pill as a Popover anchored to the whole box (composer/Island.tsx's own
 * pattern), not the chevron alone. Oldest at the top, the newest nearest the
 * input; scrolling near the top loads the next older page and the reader's
 * place holds. `PillHistory` is the always-mounted shell, so a close can
 * animate out (Radix Presence, the data-state classes already on
 * ui/popover.tsx's PopoverContent); `PillHistoryCard` is the frame that
 * actually mounts on open — the head, the log, the top slot and the jump to
 * the latest. A row is history/.
 *
 * Stillness: the card has one fixed height from the moment it opens, so the
 * first page landing never grows it again, and no row animates in.
 */
import {
  Fragment,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import type { TeamAgent } from "@bash/shared";
import { useComposerHistoryPages } from "@/components/home-briefing/composerHistoryRows";
import { useDockRouter } from "@/components/composer/live/useDockRouter";
import { ArrowDownIcon } from "@/components/icons/ArrowDownIcon";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { HistoryExchange } from "./history/HistoryExchange";
import { HistoryRelayout } from "./history/FoldedAnswer";
import { historyDays } from "./history/historyDays";

/** Within this of the top, the next older page is asked for. */
const LOAD_OLDER_PX = 160;
/** Further than this from the newest, the jump to latest shows. */
const JUMP_PX = 240;
/** Within this of the newest, the log counts as pinned there. */
const PINNED_PX = 24;

export function PillHistory({
  open,
  anchorRef,
  onClose,
  onRetry,
  now,
  agent,
}: {
  /** BashPill's historyOpen level, not an unmount edge: PillHistory stays
   *  mounted through a close so the popover can animate out. */
  open: boolean;
  /** The box it floats above: a virtual anchor, so the popover takes the
   *  whole pill's width and position, not just the chevron's. */
  anchorRef: RefObject<HTMLElement | null>;
  onClose: () => void;
  /** Try again on a failed ask: the pill closes the history and sends the
   *  words anew (on a task's screen it puts them in its field instead). */
  onRetry: (words: string) => void;
  /** The open gesture's timestamp: the day labels ride it so render stays pure. */
  now: number;
  /** Agents first (train 1b): the picked agent whose transcript this is, and
   *  who speaks in it (its face and name). Absent or null = Bash's. */
  agent?: Pick<TeamAgent, "id" | "kind" | "handle" | "name"> | null;
}) {
  return (
    // No onOpenChange: closing is the host's (Escape, a send — BashPill's own
    // effects), so Radix's own Escape/focus dismissal is inert (mirrors
    // composer/Island.tsx). A pointer down is the one dismissal PillHistory
    // handles itself, since unlike Island it has no host-level outside-click
    // listener of its own: anything on the pill (the chevron included) is the
    // pill's own business and closes nothing here.
    <Popover open={open}>
      <PopoverAnchor virtualRef={anchorRef as RefObject<HTMLElement>} />
      <PopoverContent
        side="top"
        align="center"
        sideOffset={10}
        avoidCollisions={false}
        // The composer keeps the caret: the popover never takes or returns focus.
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => {
          if (anchorRef.current?.contains(e.target as Node)) return;
          onClose();
        }}
        className="w-[var(--radix-popover-trigger-width)]"
      >
        {/* Gating on `now` rather than `open`: the store only ever re-stamps
            historyOpenedAt on an OPEN (never on a close, liveStore's
            toggleHistory/closeHistory), so this stays mounted, unchanged,
            through a close — Radix's Presence gets real content to animate
            out instead of an emptied frame. `key={now}` still forces a fresh
            mount (a fresh fetch, useComposerHistoryPages fetches on mount)
            the next time it genuinely opens. */}
        {now > 0 && (
          <PillHistoryCard
            key={now}
            onClose={onClose}
            onRetry={onRetry}
            now={now}
            agent={agent}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}

function PillHistoryCard({
  onClose,
  onRetry,
  now,
  agent,
}: {
  onClose: () => void;
  onRetry: (words: string) => void;
  now: number;
  agent?: Pick<TeamAgent, "id" | "kind" | "handle" | "name"> | null;
}) {
  // In the shell's Dock window a push goes to the main window (useDockRouter).
  const router = useDockRouter();
  const { rows, hasMore, loadingOlder, loadOlder, settled } =
    useComposerHistoryPages(agent?.handle);
  const speaker = agent ?? undefined;
  const days = useMemo(() => historyDays(rows, now), [rows, now]);

  const logRef = useRef<HTMLDivElement>(null);
  // Refs, not state: they are read and written between layout and paint.
  const pinned = useRef(true);
  /** scrollHeight - scrollTop as the reader last left it: their distance from the bottom. */
  const fromBottom = useRef(0);
  const [jumpOn, setJumpOn] = useState(false);
  // An older page was asked for; with none left after it, the top says so.
  const [askedOlder, setAskedOlder] = useState(false);

  /** Put the reader back: at the newest when pinned, else the same distance
   *  from the bottom (the height above them changed). Handed the element whose
   *  height changed (a fold), an unpinned reader moves only when it sits above
   *  the viewport: one below them changed nothing they see (review finding 1).
   *  Either way the distance is taken afresh, so the next hold starts true. */
  const hold = useCallback((changed?: HTMLElement) => {
    const el = logRef.current;
    if (!el) return;
    if (pinned.current) el.scrollTop = el.scrollHeight;
    else if (
      !changed ||
      changed.getBoundingClientRect().top < el.getBoundingClientRect().top
    )
      el.scrollTop = el.scrollHeight - fromBottom.current;
    fromBottom.current = el.scrollHeight - el.scrollTop;
  }, []);

  // Open pinned to the newest; a row landing while pinned stays pinned; an
  // older page prepended keeps the distance from the bottom; a row landing
  // below an unpinned reader moves nothing, and the distance is taken afresh.
  // Before paint.
  const firstId = rows[0]?.id;
  const lastId = rows[rows.length - 1]?.id;
  const shownFirst = useRef(firstId);
  useLayoutEffect(() => {
    const prepended = shownFirst.current !== firstId;
    shownFirst.current = firstId;
    if (pinned.current || prepended) hold();
    else if (logRef.current)
      fromBottom.current =
        logRef.current.scrollHeight - logRef.current.scrollTop;
  }, [firstId, lastId, hold]);

  const onScroll = (): void => {
    const el = logRef.current;
    if (!el) return;
    const below = el.scrollHeight - el.scrollTop - el.clientHeight;
    pinned.current = below < PINNED_PX;
    fromBottom.current = el.scrollHeight - el.scrollTop;
    setJumpOn(below > JUMP_PX);
    if (el.scrollTop < LOAD_OLDER_PX && hasMore && !loadingOlder) {
      setAskedOlder(true);
      loadOlder();
    }
  };

  const jump = (): void => {
    const el = logRef.current;
    if (!el) return;
    pinned.current = true;
    const still =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollTo({ top: el.scrollHeight, behavior: still ? "auto" : "smooth" });
  };

  const go = useCallback(
    (route: string) => {
      router.push(route);
      onClose();
    },
    [router, onClose]
  );

  return (
    <div
      className="bh-hist flex h-[min(60vh,520px)] min-h-0 flex-col"
      role="region"
      aria-label="Composer history"
      data-testid="composer-history-card"
    >
      <div className="bh-rec__head flex flex-none items-center gap-2 border-b border-border-default px-3.5 pt-3 pb-2 text-3.5 font-semibold">
        History
      </div>
      <div className="relative min-h-0 flex-1">
        {!settled ? (
          // A run's receipt and a task's live status are server-only
          // (composerHistoryRows.ts): the mirror can paint instantly, but a
          // row painted from it now would grow a receipt or a status line in
          // right under the reader once the real page lands. Waiting the one
          // beat for `settled` means every row paints once, already whole.
          <div
            className="flex h-full items-center justify-center gap-2 text-3.5 text-fg-subtle"
            data-testid="history-loading"
          >
            <span
              className="size-3 animate-spin-1 rounded-full border-2 border-border-muted border-t-fg-muted"
              aria-hidden="true"
            />
            Loading
          </div>
        ) : rows.length === 0 ? (
          <div className="bh-empty bh-empty--hist flex h-full items-center justify-center text-3.5 leading-normal text-fg-muted">
            {speaker
              ? `Nothing yet. Messages you send ${speaker.name} from here will show up.`
              : "Nothing yet. Quick asks you send from here will show up."}
          </div>
        ) : (
          <div
            ref={logRef}
            role="log"
            aria-label={`Your conversation with ${speaker?.name ?? "Bash"}`}
            tabIndex={0}
            onScroll={onScroll}
            className="flex h-full flex-col overflow-y-auto overscroll-contain px-3.5 pb-4 [overflow-anchor:none] [scrollbar-gutter:stable]"
          >
            <HistoryRelayout.Provider value={hold}>
              {/* mt-auto: a short history sits by the input, not at the top */}
              <div className="mt-auto flex flex-col gap-5">
                <div
                  className="flex h-10 flex-none items-center justify-center gap-2 text-3 text-fg-subtle"
                  data-testid="history-top"
                >
                  {loadingOlder ? (
                    <>
                      <span
                        className="size-3 animate-spin-1 rounded-full border-2 border-border-muted border-t-fg-muted"
                        aria-hidden="true"
                      />
                      Loading earlier messages
                    </>
                  ) : (
                    askedOlder && !hasMore && "That's the beginning"
                  )}
                </div>
                {days.map((day) => (
                  <Fragment key={day.key}>
                    <div
                      role="heading"
                      aria-level={3}
                      className="flex items-center gap-3 text-3 font-medium text-fg-subtle before:h-px before:flex-1 before:bg-border-default before:content-[''] after:h-px after:flex-1 after:bg-border-default after:content-['']"
                    >
                      {day.label}
                    </div>
                    {/* `key` over `id`: a server row that settled a legacy
                        mirror row keeps the mirror row's node (mergeHistory) */}
                    {day.rows.map((entry) => (
                      <HistoryExchange
                        key={entry.key ?? entry.id}
                        entry={entry}
                        onGo={go}
                        onRetry={onRetry}
                        speaker={speaker}
                      />
                    ))}
                  </Fragment>
                ))}
              </div>
            </HistoryRelayout.Provider>
          </div>
        )}
        <button
          type="button"
          aria-label="Jump to latest"
          inert={!jumpOn}
          onClick={jump}
          className={cn(
            "transition-bh-key absolute bottom-2.5 left-1/2 grid size-8 -translate-x-1/2 cursor-pointer place-items-center rounded-full border border-border-default bg-bg-elevated text-fg-muted shadow-md hover:text-fg-default",
            jumpOn ? "translate-y-0 opacity-100" : "translate-y-1.5 opacity-0"
          )}
        >
          <ArrowDownIcon aria-hidden="true" className="size-3.5" />
        </button>
      </div>
    </div>
  );
}
